import React, {useMemo} from 'react';

import {Class, Feature, SubClass, ImageTarget} from '../../../generated/types';
import {PageTools} from '../../../components/page-tools';
import {Breadcrumbs} from '../../../components/breadcrumbs';
import {EditHoc} from '../../../components/edit-hoc';
import {Markdown} from '../../../components/utils/md';
import {FeatureParentObject, FeatureSeo, FeatureView, groupFeatures} from '../../../components/feature-view';
import {buildUrl, useRoute} from '../../../lib/routes';
import {useFeatureModal} from '../../../components/use-feature-modal';
import {cls} from '../../../components/utils';
import {MainImage} from '../../../components/main-image';
import {Gallery} from '../../../components/gallery';
import {OtherTitles} from '../../../components/other-titles';
import {CommentTargetType, Comments} from '../../../components/comments';

import dashboardCss from '@kit/sidebar/dashboard.module.css';
import pageCss from '../../../components/common.module.css';
import markdownCss from '../../../components/utils/md/markdown.module.css';
import css from './sub-class-view.module.css';

type Props = {
	subClass: SubClass;
	classId: Class['id'];
	subClassId: SubClass['id'];
};

export const SubClassView: React.FC<Props> = ({subClass, classId, subClassId}) => {
	if (!subClass) return null;

	const features = useMemo(() => {
		if (subClass) return groupFeatures(subClass.features as Feature[]);
		return [];
	}, [subClass]);
	const baseFeatures = useMemo(() => features.filter((f) => !f.parentId), [features]);
	const featureParent: FeatureParentObject = {
		type: 'subClass',
		parentId: subClassId,
		supParentId: classId,
	};

	const FeatureModal = useFeatureModal({
		features,
		route: 'subClass',
		params: {id: classId, subClassId},
		featureParent,
	});

	const mainImage = subClass?.images?.at(0);

	const {routeName, query} = useRoute();
	const openFeature =
		routeName === 'subClassFeature'
			? subClass.features?.find((feature) => feature.id === query.featureId)
			: undefined;
	const subClassPath = buildUrl('subClass', {id: classId, subClassId});

	return (
		<div className={dashboardCss.view}>
			{openFeature && (
				<FeatureSeo
					feature={openFeature}
					section="classes"
					owner={`умение подкласса ${subClass.title}`}
					lead={`Умение подкласса ${subClass.title} класса ${subClass.classTitle}`}
					canonicalPath={subClassPath}
					crumbs={[
						{name: subClass.classTitle, path: buildUrl('class', {id: classId})},
						{name: subClass.title, path: subClassPath},
						{
							name: openFeature.title,
							path: buildUrl('subClassFeature', {id: classId, subClassId, featureId: openFeature.id}),
						},
					]}
				/>
			)}
			{FeatureModal}
			<article className={dashboardCss.viewWrapper}>
				<PageTools />
				<h1 className={cls(dashboardCss.mainTitle, dashboardCss.hidden)}>{subClass.title}</h1>
				<Breadcrumbs
					breadcrumbs={[
						{title: subClass.classTitle, route: 'subClasses', params: {id: classId}},
						{
							title: subClass.title,
							subTitle: <em>{subClass.caption}</em>,
						},
					]}
					className={dashboardCss.mainTitle}
				/>
				<div className={dashboardCss.page}>
					<div className={css.container}>
						<div className={css.wrapper}>
							{mainImage && (
								<div className={css.image}>
									<MainImage image={mainImage} size={Number.POSITIVE_INFINITY} target={{type: ImageTarget.SubClass, id: subClassId}} />
								</div>
							)}
							<div className={css.body}>
								<EditHoc
									load={() => import('../edit-buttons').then((m) => m.EditSubClass)}
									editProps={{classId, id: subClassId}}
								>
									<Markdown text={subClass.description} />
								</EditHoc>
								<div className={pageCss.columns}>
									{baseFeatures.map((feature) => (
										<FeatureView feature={feature} parentObject={featureParent} key={feature.id} />
									))}
								</div>
								<div className={markdownCss.margin}>
									<div className={markdownCss.indent}>
										<OtherTitles
											originalTitle={subClass.originalTitle}
											alternativeTitles={subClass.alternativeTitles}
										/>
									</div>
								</div>
							</div>
						</div>
					</div>
					<Gallery
						images={subClass.images || []}
						title={'Галерея'}
						startIndex={1}
						target={{type: ImageTarget.SubClass, id: subClassId}}
					/>
					<Comments targetType={CommentTargetType.SubClass} targetId={subClassId} />
				</div>
			</article>
		</div>
	);
};
